import { memo, useCallback, useMemo, useState } from 'react'
import {
  Box, Button, Checkbox, CloseButton, Dialog, HStack, Input, InputGroup, Spinner, Text, VStack,
} from '@chakra-ui/react'
import { FiSearch } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../shared'
import {
  useBusinessSearch, useCopyArrivalInstruction, useWorksiteSearch, type ArrivalKind,
} from '../../hooks/useBusiness'
import { useLimits } from '../../hooks/useLimits'
import { extractApiMessage } from '../../lib/apiError'
import type { CopyArrivalInstructionResult } from '../../types/business'

const PAGE_SIZE = 50

interface Row { id: string; label: string; meta?: string }

// Memoized so toggling one row re-renders only that row (a selection can reach 200).
const TargetRow = memo(function TargetRow({ row, checked, onToggle }: {
  row: Row; checked: boolean; onToggle: (id: string) => void
}) {
  return (
    <Checkbox.Root
      checked={checked}
      onCheckedChange={() => onToggle(row.id)}
      minH="44px"
      px={3}
      py={2}
      w="full"
    >
      <Checkbox.HiddenInput />
      <Checkbox.Control />
      <Checkbox.Label flex={1}>
        <Box>
          <Text fontSize="sm" fontWeight="medium">{row.label}</Text>
          {row.meta && <Text fontSize="xs" color="fg.muted">{row.meta}</Text>}
        </Box>
      </Checkbox.Label>
    </Checkbox.Root>
  )
})

/** O3: copy this record's own arrival instruction to other worksites (or other businesses).
 *  Continue runs a dry run to show how many existing instructions would be replaced; Copy runs it. */
export function CopyArrivalModal({ kind, sourceId, open, onClose }: {
  kind: ArrivalKind; sourceId: string; open: boolean; onClose: () => void
}) {
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [preview, setPreview] = useState<CopyArrivalInstructionResult | null>(null)

  const params = { search: searchTerm || undefined, page: 1, pageSize: PAGE_SIZE }
  const worksites = useWorksiteSearch({ ...params, isActive: true }, { enabled: open && kind === 'worksite' })
  const businesses = useBusinessSearch(params, { enabled: open && kind === 'business' })
  const isLoading = kind === 'worksite' ? worksites.isLoading : businesses.isLoading

  const worksiteItems = worksites.data?.items
  const businessItems = businesses.data?.items
  // Memoized so row objects stay stable across selection changes (TargetRow is memoized).
  const visibleRows = useMemo<Row[]>(() => {
    const rows: Row[] = kind === 'worksite'
      ? (worksiteItems ?? []).map(w => ({
          id: w.id, label: w.label, meta: [w.city, w.state].filter(Boolean).join(', ') || undefined,
        }))
      : (businessItems ?? []).map(b => ({ id: b.id, label: b.label }))
    return rows.filter(r => r.id !== sourceId)
  }, [kind, worksiteItems, businessItems, sourceId])

  const copy = useCopyArrivalInstruction(kind, sourceId)
  const copyMutate = copy.mutate
  const copyReset = copy.reset
  // Served cap, pre-flight warning only: unknown while limits load/error -> no warning, and the
  // server's 400 (via extractApiMessage) stays the only gate.
  const targetCap = useLimits().data?.inputCaps.maxArrivalCopyTargets
  const overCap = targetCap !== undefined && selected.length > targetCap

  const toggle = useCallback((id: string) => {
    copyReset() // a changed selection makes any earlier dry-run error stale
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }, [copyReset])
  const selectedSet = new Set(selected)

  const handleContinue = () => {
    copyMutate({ targetIds: selected, dryRun: true }, { onSuccess: result => setPreview(result) })
  }

  const handleCopy = () => {
    copyMutate({ targetIds: selected, dryRun: false }, {
      onSuccess: result => {
        toaster.create({ title: `Copied to ${result.copied}`, type: 'success', duration: 3000 })
        onClose()
      },
    })
  }

  const handleBack = () => {
    copy.reset()
    setPreview(null)
  }

  const noun = kind === 'worksite' ? 'worksites' : 'businesses'

  return (
    <ResponsiveModal isOpen={open} onClose={onClose} size="lg" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Copy arrival instruction</Dialog.Title>
          </Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            {preview ? (
              <Text>
                {`Copy to ${preview.copied}.`}
                {preview.replaced > 0 ? ` This replaces ${preview.replaced} existing instruction(s).` : ''}
              </Text>
            ) : (
              <VStack align="stretch" gap={3}>
                <HStack gap={2}>
                  <InputGroup startElement={<FiSearch />}>
                    <Input
                      minH="44px"
                      placeholder={`Search ${noun}...`}
                      aria-label={`Search ${noun}`}
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') setSearchTerm(searchInput) }}
                    />
                  </InputGroup>
                  <Button minH="44px" colorPalette="brand" onClick={() => setSearchTerm(searchInput)}>Search</Button>
                </HStack>

                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" fontWeight="medium">{`${selected.length} selected`}</Text>
                  {overCap && <Text fontSize="sm" color="danger.fg">{`Up to ${targetCap} at a time`}</Text>}
                </HStack>

                {isLoading && (
                  <VStack py={6}><Spinner size="md" color="accent" /></VStack>
                )}
                {!isLoading && visibleRows.length === 0 && (
                  <Text fontSize="sm" color="fg.muted" textAlign="center" py={4}>
                    {`No ${noun} found.`}
                  </Text>
                )}
                {!isLoading && visibleRows.length > 0 && (
                  <VStack align="stretch" gap={0} borderWidth="1px" borderColor="border" borderRadius="md">
                    {visibleRows.map(r => {
                      const checked = selectedSet.has(r.id)
                      return (
                        <TargetRow key={r.id} row={r} checked={checked} onToggle={toggle} />
                      )
                    })}
                  </VStack>
                )}
              </VStack>
            )}

            {!!copy.error && (
              <Text mt={3} fontSize="sm" color="danger.fg">
                {extractApiMessage(copy.error, 'Could not copy the arrival instruction')}
              </Text>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            {preview ? (
              <>
                <Button variant="ghost" minH="44px" onClick={handleBack}>Back</Button>
                <Button colorPalette="brand" minH="44px" loading={copy.isPending} onClick={handleCopy}>Copy</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" minH="44px" onClick={onClose}>Cancel</Button>
                <Button
                  colorPalette="brand"
                  minH="44px"
                  disabled={selected.length === 0}
                  loading={copy.isPending}
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              </>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
